import { useEffect, useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { escapeHtml } from "../lib/sanitize";
import { storeName } from "../lib/labels";
import DateRangePicker from "./DateRangePicker";

function stripTee(name) {
  return name.replace(/^\s*#Printed\s*Tee\s*[-–:]\s*/i, "").trim();
}

function PairRow({ pair, maxCount, rank }) {
  const pct = maxCount ? Math.round((pair.count / maxCount) * 100) : 0;
  return (
    <div className="pt-2 pb-2 border-t border-[#0a0a0a]/15" data-testid={`basket-pair-row-${rank}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-medium">{stripTee(pair.product_a)}</span>
          <span className="text-neutral-500 mx-1.5">+</span>
          <span className="font-medium">{stripTee(pair.product_b)}</span>
        </div>
        <div className="font-mono-bm text-sm font-semibold tabular-nums whitespace-nowrap">
          {pair.count.toLocaleString()}
          <span className="bm-eyebrow text-neutral-500 ml-1">baskets</span>
        </div>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <div className="h-[2px] bg-[#0a0a0a]/10 flex-1">
          <div className="h-full bg-[#0a0a0a]" style={{ width: `${pct}%` }} />
        </div>
        <span className="bm-eyebrow text-neutral-500 font-mono-bm w-12 text-right">{pair.share_pct}%</span>
      </div>
    </div>
  );
}

function StoreColumn({ store }) {
  const pairs = store.pairs || [];
  const maxCount = Math.max(1, ...pairs.map((p) => p.count));
  return (
    <div className="border border-[#0a0a0a]/60 bg-[#f5f2eb] p-4" data-testid={`basket-store-${storeName(store.name)}`}>
      <div className="flex items-baseline justify-between mb-1">
        <div className="bm-headline text-lg">{storeName(store.name)}</div>
        <div className="bm-eyebrow text-neutral-500 font-mono-bm">
          {store.basket_count.toLocaleString()} baskets
        </div>
      </div>
      <div className="bm-eyebrow text-neutral-500 mb-2">Top item pairs</div>
      {pairs.length === 0 && (
        <div className="text-sm text-neutral-500 pt-3 border-t border-[#0a0a0a]/15">
          Not enough multi-item baskets in this window.
        </div>
      )}
      {pairs.map((p, i) => (
        <PairRow key={`${p.product_a}|${p.product_b}`} pair={p} maxCount={maxCount} rank={i} />
      ))}
    </div>
  );
}

/** Very light markdown → JSX (headers + bullets only, matches the playbook prompt). */
function renderPlaybook(md) {
  if (!md) return null;
  const lines = md.split(/\r?\n/);
  const out = [];
  let ulBuf = [];
  const flushUl = () => {
    if (ulBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2 text-sm">
          {ulBuf.map((t, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(t) }} />
          ))}
        </ul>
      );
      ulBuf = [];
    }
  };
  const inline = (t) =>
    escapeHtml(t).replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-[#0a0a0a]">$1</strong>')
     .replace(/\*([^*]+)\*/g, "<em>$1</em>")
     .replace(/`([^`]+)`/g, '<code class="font-mono-bm text-[13px]">$1</code>');

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (/^###\s+/.test(line)) {
      flushUl();
      const title = line.replace(/^###\s+/, "").trim();
      out.push(
        <h4 key={`h-${out.length}`} className="bm-headline text-lg mt-5 pt-4 border-t border-[#0a0a0a]/20 first:border-t-0 first:mt-0 first:pt-0">
          {title}
        </h4>
      );
    } else if (/^##\s+/.test(line)) {
      flushUl();
      out.push(
        <h3 key={`h2-${out.length}`} className="bm-headline text-2xl mt-2">{line.replace(/^##\s+/, "")}</h3>
      );
    } else if (/^#\s+/.test(line)) {
      flushUl();
      out.push(
        <h2 key={`h1-${out.length}`} className="bm-headline text-3xl mt-2">{line.replace(/^#\s+/, "")}</h2>
      );
    } else if (/^\s*[-*]\s+/.test(line)) {
      ulBuf.push(line.replace(/^\s*[-*]\s+/, ""));
    } else if (/^---+$/.test(line.trim())) {
      flushUl();
      // horizontal rules are absorbed into our ### section breaks — skip
    } else if (line.trim() === "") {
      flushUl();
    } else {
      flushUl();
      out.push(
        <p key={`p-${out.length}`} className="text-sm text-neutral-800 my-2" dangerouslySetInnerHTML={{ __html: inline(line) }} />
      );
    }
  }
  flushUl();
  return out;
}

export default function BasketAnalyser() {
  const [range, setRange] = useState(null);
  const [data, setData] = useState({ by_outlet: [] });
  const [loading, setLoading] = useState(false);
  const [playbook, setPlaybook] = useState(null);
  const [pbLoading, setPbLoading] = useState(false);

  useEffect(() => {
    if (!range) return;
    let cancel = false;
    setLoading(true);
    setPlaybook(null); // reset when window changes
    // Load pairs + latest cached playbook in parallel
    Promise.all([
      api.get("/basket/pairs", { params: { start_date: range.start, end_date: range.end, limit_per_store: 8 } }),
      api.get("/basket/playbook/latest", { params: { start_date: range.start, end_date: range.end } }),
    ]).then(([p, c]) => {
      if (cancel) return;
      setData(p.data);
      if (c.data?.status === "ok") {
        setPlaybook(c.data);
      } else if ((p.data?.by_outlet || []).some((s) => (s.pairs || []).length > 0)) {
        // Auto-generate for this window since we don't have a cached playbook yet.
        generatePlaybook(false);
      }
    }).catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.start, range?.end]);

  async function generatePlaybook(refresh = false) {
    if (!range) return;
    setPbLoading(true);
    try {
      const r = await api.post("/basket/playbook", null, {
        params: { start_date: range.start, end_date: range.end, refresh },
      });
      if (r.data?.status === "ok") setPlaybook(r.data);
    } catch (e) {
      /* no-op — surface via UI if needed */
    } finally {
      setPbLoading(false);
    }
  }

  const stores = data.by_outlet || [];
  const hasData = stores.some((s) => (s.pairs || []).length > 0);

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="basket-analyser">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-2">
        <div className="bm-eyebrow">Table 16 &middot; Cross-sell</div>
        <div data-testid="basket-date-picker">
          <DateRangePicker onChange={setRange} />
        </div>
      </div>
      <h3 className="bm-headline text-3xl mb-1">Basket analyser.</h3>
      <p className="bm-eyebrow text-neutral-500 mb-5">
        Items bought together in the same basket &middot; per store &middot; use for merchandising & bundling
      </p>

      {loading && stores.length === 0 && (
        <div className="text-sm text-neutral-500">Mining baskets…</div>
      )}

      {!loading && stores.length === 0 && (
        <div className="text-sm text-neutral-600">No basket data available.</div>
      )}

      {stores.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {stores.map((s) => (
            <StoreColumn key={s.outlet_id} store={s} />
          ))}
        </div>
      )}

      {/* Claude cross-sell playbook */}
      {hasData && (
        <div className="mt-6 border border-[#0a0a0a] bg-[#f5f2eb] p-5" data-testid="basket-playbook">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="bm-eyebrow text-neutral-500 mb-1">Table 17 &middot; Playbook</div>
              <h4 className="bm-headline text-2xl">Cross-sell playbook.</h4>
              <p className="bm-eyebrow text-neutral-500 mt-1">
                Claude reads the top pairs for each store and writes the moves.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {playbook?.generated_at && (
                <div className="bm-eyebrow text-neutral-500 font-mono-bm">
                  {new Date(playbook.generated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </div>
              )}
              {!playbook ? (
                <button
                  data-testid="basket-generate-playbook"
                  onClick={() => generatePlaybook(false)}
                  disabled={pbLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bm-eyebrow border border-[#0a0a0a] bg-[#0a0a0a] text-[#f5f2eb] hover:opacity-90 disabled:opacity-50"
                >
                  <Sparkles size={13} />
                  {pbLoading ? "Writing…" : "Generate playbook"}
                </button>
              ) : (
                <button
                  data-testid="basket-refresh-playbook"
                  onClick={() => generatePlaybook(true)}
                  disabled={pbLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bm-eyebrow border border-[#0a0a0a] hover:bg-[#0a0a0a]/5 disabled:opacity-50"
                >
                  <RefreshCw size={13} className={pbLoading ? "animate-spin" : ""} />
                  {pbLoading ? "Rewriting…" : "Rewrite"}
                </button>
              )}
            </div>
          </div>

          {playbook?.markdown ? (
            <div data-testid="basket-playbook-content">{renderPlaybook(playbook.markdown)}</div>
          ) : (
            <div className="text-sm text-neutral-600">
              {pbLoading
                ? "Claude is thinking through the basket data…"
                : "No playbook yet. Click Generate playbook to have Claude turn these pairs into per-store moves."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
